/**
 * Paragraph Motion — Non-uniform scroll entrance system
 * Vanilla JS Web Component, IntersectionObserver-based.
 *
 * Produces varied, organic-feeling reveal animations per section type.
 * No AOS, no uniform stagger, no bounce.
 *
 * Usage: <paragraph-motion></paragraph-motion>
 * Place once in theme.liquid (before </body>). It self-initialises
 * and discovers every section on the page.
 */

(() => {
  'use strict';

  /* --------------------------------------------------------
     Helpers
     -------------------------------------------------------- */

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** True when Shopify theme editor is active */
  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  /** Remove will-change hint after animation completes */
  function clearWillChange(el) {
    const dur = parseFloat(getComputedStyle(el).transitionDuration) * 1000 || 1000;
    const delay = parseFloat(getComputedStyle(el).transitionDelay) * 1000 || 0;
    setTimeout(() => {
      el.classList.remove('pm-animating', 'pm-animating-clip');
    }, dur + delay + 50);
  }

  /**
   * Strip Dawn's legacy scroll-trigger animation classes from elements
   * paragraph-motion is taking over. Without this, both systems run on the
   * same element (e.g. .multicolumn-list__item) — wasted compositing + stutter.
   * Call once per element before stamping our own data-pm attributes.
   */
  function dropDawnScrollTrigger(el) {
    el.classList.remove(
      'scroll-trigger',
      'scroll-trigger--offscreen',
      'scroll-trigger--cancel',
      'animate--slide-in',
      'animate--fade-in'
    );
    // Also clear the inline --animation-order CSS var Dawn sets
    el.style.removeProperty('--animation-order');
  }

  /**
   * Set custom-property timing on an element.
   * @param {Element} el
   * @param {number}  delayMs
   * @param {number}  [durMs]  — optional override
   */
  function setTiming(el, delayMs, durMs) {
    el.style.setProperty('--pm-delay', `${Math.round(delayMs)}ms`);
    if (durMs !== undefined) {
      el.style.setProperty('--pm-dur', `${Math.round(durMs)}ms`);
    }
  }

  /**
   * Return a slightly jittered value so staggers don't feel robotic.
   * Adds +-15 % randomness clamped to a floor.
   */
  function jitter(base, amount = 0.15) {
    const offset = base * amount * (Math.random() * 2 - 1);
    return Math.max(0, base + offset);
  }

  /* --------------------------------------------------------
     Section-specific setup functions
     These stamp data-pm / data-pm-child attributes and set
     per-element timing. Called once per section before
     observing.
     -------------------------------------------------------- */

  const setup = {

    /**
     * Hero — gentle cascade on text children.
     * Heading materialises (opacity + subtle scale, done in CSS).
     *
     * If the hero-intro animation is active (first page load),
     * skip paragraph-motion setup so the intro plays uninterrupted.
     */
    hero(section) {
      const content = section.querySelector('.pw-hero-homepage__content');
      if (!content) return [];

      // Hero intro animation owns the hero's reveal (on first load and
      // when fast-forwarded via the session gate). Skip paragraph-motion
      // entirely — stamping data-pm-child here would hide the CTAs.
      if (
        content.classList.contains('hero-intro-active') ||
        content.classList.contains('hero-intro-done') ||
        content.hasAttribute('data-hero-intro')
      ) {
        return [];
      }

      const children = [
        { el: content.querySelector('.pw-hero-homepage__caption'), role: 'caption' },
        { el: content.querySelector('.pw-hero-homepage__heading'), role: 'heading' },
        { el: content.querySelector('.pw-hero-homepage__text'),    role: 'text' },
        { el: content.querySelector('.pw-hero-homepage__buttons'), role: 'buttons' },
      ].filter(c => c.el);

      // Non-uniform delays: caption 0, heading 220, text 480, buttons 680
      const delays = [0, 220, 480, 680];
      const durations = [650, 900, 750, 700];

      children.forEach((c, i) => {
        c.el.setAttribute('data-pm-child', c.role);
        setTiming(c.el, delays[i] || 0, durations[i]);
        c.el.classList.add('pm-animating');
      });

      // The container gets .is-visible; children animate via parent selector
      return [{ el: content, type: 'parent' }];
    },

    /**
     * Multicolumn — each column observed independently with
     * irregular stagger. Center-column can arrive first if 3 cols.
     */
    multicolumn(section) {
      const targets = [];

      // Strip Dawn's scroll-trigger from the section root + inner padding wrapper.
      // Dawn applies the class to multiple ancestors; we own this section now.
      dropDawnScrollTrigger(section);
      section.querySelectorAll('.scroll-trigger').forEach(dropDawnScrollTrigger);

      // Section heading
      const heading = section.querySelector('.title-wrapper-with-link');
      if (heading) {
        heading.setAttribute('data-pm', 'heading');
        heading.classList.add('pm-animating');
        targets.push({ el: heading, type: 'self' });
      }

      const items = Array.from(section.querySelectorAll('.multicolumn-list__item'));
      const count = items.length;

      // Decide stagger pattern based on column count
      let delays;
      if (count === 3) {
        // Center arrives slightly first, then left, then right
        delays = [120, 0, 260];
      } else if (count === 2) {
        delays = [0, 160];
      } else {
        // 4+ : left-to-right with irregular spacing
        delays = items.map((_, i) => {
          const base = i * 150;
          return jitter(base, 0.2);
        });
      }

      const baseDur = 750;

      items.forEach((item, i) => {
        dropDawnScrollTrigger(item);
        item.setAttribute('data-pm-child', `col-${i}`);
        setTiming(item, delays[i] || 0, jitter(baseDur, 0.08));
        item.classList.add('pm-animating');
        targets.push({ el: item, type: 'self' });
      });

      return targets;
    },

    /**
     * Featured collection — product cards scale-up.
     * Stagger feels natural: irregular gaps between reveals.
     */
    featuredCollection(section) {
      const targets = [];

      // Section heading / description
      const titleWrap = section.querySelector('.collection__title, .title-wrapper');
      if (titleWrap) {
        const h2 = titleWrap.querySelector('h2, .title');
        if (h2) {
          h2.setAttribute('data-pm', 'heading');
          h2.classList.add('pm-animating');
          targets.push({ el: h2, type: 'self' });
        }
        const desc = titleWrap.querySelector('.collection__description');
        if (desc) {
          desc.setAttribute('data-pm', 'fade');
          setTiming(desc, 120);
          desc.classList.add('pm-animating');
          targets.push({ el: desc, type: 'self' });
        }
      }

      // Product cards — each grid__item
      const cards = Array.from(section.querySelectorAll('.product-grid > .grid__item, .grid.product-grid > .grid__item'));

      // Irregular stagger: 0, 140, 320, 440, 620 ...
      const gaps = [0, 140, 180, 120, 180, 140, 160, 120];
      let cumulative = 0;

      cards.forEach((card, i) => {
        dropDawnScrollTrigger(card);
        cumulative += (gaps[i] || jitter(150, 0.2));
        const delay = i === 0 ? 0 : cumulative;
        card.setAttribute('data-pm-child', `card-${i}`);
        setTiming(card, delay, jitter(700, 0.1));
        card.classList.add('pm-animating');
        targets.push({ el: card, type: 'self' });
      });

      return targets;
    },

    /**
     * Image with text — image and text animated independently.
     * Image: clip-path reveal. Text: heading first, body, CTA.
     */
    imageWithText(section) {
      const targets = [];

      // Strip Dawn's scroll-trigger from the section root + media + text
      dropDawnScrollTrigger(section);

      // Image side
      const mediaItem = section.querySelector('.image-with-text__media-item');
      if (mediaItem) {
        dropDawnScrollTrigger(mediaItem);
        mediaItem.setAttribute('data-pm', 'image');
        setTiming(mediaItem, 0, 900);
        mediaItem.classList.add('pm-animating-clip');
        targets.push({ el: mediaItem, type: 'self' });
      }

      // Text side — children cascade
      const textContainer = section.querySelector('.image-with-text__content');
      if (textContainer) {
        const textChildren = Array.from(textContainer.children);
        // Heading 0ms, caption/text ~240ms, button ~500ms
        const delayMap = {
          heading: 80,
          caption: 160,
          text: 300,
          button: 520,
        };
        const durMap = {
          heading: 800,
          caption: 700,
          text: 750,
          button: 650,
        };

        textChildren.forEach((child) => {
          dropDawnScrollTrigger(child);
          // Identify role from Dawn's class names
          let role = 'text';
          if (child.classList.contains('image-with-text__heading') || child.tagName === 'H2') role = 'heading';
          else if (child.classList.contains('caption-with-letter-spacing') || child.classList.contains('subtitle')) role = 'caption';
          else if (child.tagName === 'A' || child.classList.contains('button')) role = 'button';
          else if (child.classList.contains('image-with-text__text') || child.classList.contains('rte')) role = 'text';

          child.setAttribute('data-pm-child', role);
          setTiming(child, delayMap[role] || 200, durMap[role] || 750);
          child.classList.add('pm-animating');
          targets.push({ el: child, type: 'self' });
        });
      }

      return targets;
    },

    /**
     * Newsletter — the whole wrapper fades in with slight scale.
     * Simple and quiet.
     */
    newsletter(section) {
      const wrapper = section.querySelector('.newsletter__wrapper');
      if (!wrapper) return [];

      dropDawnScrollTrigger(section);
      dropDawnScrollTrigger(wrapper);
      wrapper.setAttribute('data-pm', 'wrapper');
      setTiming(wrapper, 0, 800);
      wrapper.classList.add('pm-animating');
      return [{ el: wrapper, type: 'self' }];
    },

    /* --------------------------------------------------------
       Brand-section setups (paragraph-* and pw-*)
       Each follows the same opacity-based reveal contract used
       by the Dawn-native setups above. Reduced-motion is handled
       by the global @media block in paragraph-motion.css.
       -------------------------------------------------------- */

    /**
     * Generic eyebrow → heading → body cascade.
     * Used by closer, faq, reviews — sections with a clear header block.
     */
    headerCascade(section) {
      const targets = [];
      const candidates = [
        section.querySelector('[class$="__eyebrow"], .pw-eyebrow'),
        section.querySelector('[class$="__title"], [class$="__heading"], h1, h2'),
        section.querySelector('[class$="__intro"], [class$="__lede"], [class$="__sub"], .pw-lede'),
      ].filter(Boolean);

      const delays = [0, 160, 360];
      const durations = [650, 850, 750];

      candidates.forEach((el, i) => {
        el.setAttribute('data-pm-child', `cascade-${i}`);
        setTiming(el, delays[i] || 0, durations[i] || 750);
        el.classList.add('pm-animating');
        targets.push({ el, type: 'self' });
      });

      return targets;
    },

    /**
     * Process — left-aligned step cascade.
     * Each step (01, 02, 03, …) reveals with irregular stagger.
     */
    process(section) {
      const targets = [];

      // Header
      const head = section.querySelector('.pw-process__head, .pw-process__intro');
      if (head) {
        head.setAttribute('data-pm', 'fade');
        setTiming(head, 0, 800);
        head.classList.add('pm-animating');
        targets.push({ el: head, type: 'self' });
      }

      // Steps
      const steps = Array.from(section.querySelectorAll('.pw-process__step'));
      const baseDelay = head ? 200 : 0;
      const gaps = [0, 180, 220, 200, 240];
      let cumulative = baseDelay;

      steps.forEach((step, i) => {
        cumulative += (gaps[i] || jitter(200, 0.2));
        step.setAttribute('data-pm-child', `step-${i}`);
        setTiming(step, cumulative, jitter(800, 0.08));
        step.classList.add('pm-animating');
        targets.push({ el: step, type: 'self' });
      });

      return targets;
    },

    /**
     * Pairings — grid of cards, irregular reveal stagger.
     */
    pairings(section) {
      const targets = [];

      const head = section.querySelector('.pw-pairings__head, .pw-pairings__intro');
      if (head) {
        head.setAttribute('data-pm', 'fade');
        setTiming(head, 0, 800);
        head.classList.add('pm-animating');
        targets.push({ el: head, type: 'self' });
      }

      const cards = Array.from(section.querySelectorAll('.paragraph-pairing'));
      const baseDelay = head ? 180 : 0;
      const gaps = [0, 140, 180, 120];
      let cumulative = baseDelay;

      cards.forEach((card, i) => {
        cumulative += (gaps[i] || jitter(150, 0.2));
        card.setAttribute('data-pm-child', `card-${i}`);
        setTiming(card, cumulative, jitter(750, 0.1));
        card.classList.add('pm-animating');
        targets.push({ el: card, type: 'self' });
      });

      return targets;
    },

    /**
     * Tasting — header + columnar reveal (Nose / Palate / Finish).
     */
    tasting(section) {
      const targets = [];

      const head = section.querySelector('.paragraph-tasting__head, .paragraph-tasting__heading');
      if (head) {
        head.setAttribute('data-pm', 'fade');
        setTiming(head, 0, 800);
        head.classList.add('pm-animating');
        targets.push({ el: head, type: 'self' });
      }

      const columns = Array.from(section.querySelectorAll('.paragraph-tasting__column'));
      // Center-first reveal for 3-column tasting layout
      const delays = columns.length === 3 ? [220, 100, 340] : columns.map((_, i) => 100 + i * 160);

      columns.forEach((col, i) => {
        col.setAttribute('data-pm-child', `tasting-col-${i}`);
        setTiming(col, delays[i] || 0, jitter(800, 0.08));
        col.classList.add('pm-animating');
        targets.push({ el: col, type: 'self' });
      });

      return targets;
    },

    /**
     * Winemaker / Story — image clip-path reveal + text cascade.
     * Same contract as imageWithText but on .paragraph-winemaker structure.
     */
    winemaker(section) {
      const targets = [];

      const media = section.querySelector('.paragraph-winemaker__media, .paragraph-winemaker__image, .paragraph-winemaker__placeholder');
      if (media) {
        media.setAttribute('data-pm', 'image');
        setTiming(media, 0, 900);
        media.classList.add('pm-animating-clip');
        targets.push({ el: media, type: 'self' });
      }

      const text = section.querySelector('.paragraph-winemaker__text, .paragraph-winemaker__content');
      if (text) {
        const children = Array.from(text.children).slice(0, 5);
        children.forEach((c, i) => {
          c.setAttribute('data-pm-child', `text-${i}`);
          setTiming(c, 200 + i * 140, 750);
          c.classList.add('pm-animating');
          targets.push({ el: c, type: 'self' });
        });
      }

      return targets;
    },

    /**
     * Generic pw-* section — single-element fade.
     * Catch-all for the wireframe layer (manifesto bands, hero blurs,
     * pillar panels, text-image splits). Each section opts in by
     * having a class starting with 'pw-'.
     */
    pwGeneric(section) {
      // Skip if it's a section we have a more specific setup for
      if (section.classList.contains('pw-hero-homepage')) return [];

      // Don't double-stamp
      if (section.querySelector('[data-pm], [data-pm-child]')) return [];

      // Heading + first paragraph cascade if present
      const heading = section.querySelector('h1, h2, h3, .pw-h1, .pw-h2, .pw-h3, .pw-quote, [class*="__heading"], [class*="__title"]');
      const body    = section.querySelector('p, .pw-body, [class*="__text"], [class*="__body"]');

      const targets = [];

      if (heading) {
        heading.setAttribute('data-pm-child', 'pw-heading');
        setTiming(heading, 0, 850);
        heading.classList.add('pm-animating');
        targets.push({ el: heading, type: 'self' });
      }

      if (body && body !== heading) {
        body.setAttribute('data-pm-child', 'pw-body');
        setTiming(body, 200, 750);
        body.classList.add('pm-animating');
        targets.push({ el: body, type: 'self' });
      }

      // If neither, fade the whole section quietly
      if (targets.length === 0) {
        section.setAttribute('data-pm', 'fade');
        setTiming(section, 0, 800);
        section.classList.add('pm-animating');
        targets.push({ el: section, type: 'self' });
      }

      return targets;
    },
  };

  /* --------------------------------------------------------
     Web Component
     -------------------------------------------------------- */

  class ParagraphMotion extends HTMLElement {

    constructor() {
      super();
      this._observers = [];
    }

    connectedCallback() {
      // Bail: reduced motion or design mode
      if (REDUCED_MOTION || isDesignMode()) {
        this._showEverything();
        return;
      }

      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._init());
      } else {
        this._init();
      }

      // Shopify editor events: reveal everything when editor opens
      document.addEventListener('shopify:section:select', () => this._showEverything());
      document.addEventListener('shopify:section:load', () => {
        // Re-scan on section load (editor drag-and-drop)
        this._destroy();
        this._init();
      });
    }

    disconnectedCallback() {
      this._destroy();
    }

    _destroy() {
      this._observers.forEach(obs => obs.disconnect());
      this._observers = [];
    }

    _init() {
      // pw-* sections all have a root <section> element whose first class
      // is `pw-section`, `pw-hero`, `pw-bleed`, or `pw-pullquote`, OR a
      // BEM-style class matching `pw-{name}--{section.id}`. Listed explicitly
      // so we don't match child elements (e.g. `.pw-card__title`).
      const PW_SECTION_NAMES = [
        'pw-pillar-panel', 'pw-the-wine-hero', 'pw-stats-strip', 'pw-people-grid',
        'pw-press-grid', 'pw-partners-grid', 'pw-text-image-split', 'pw-fullbleed-card',
        'pw-asym-card', 'pw-hero-blur', 'pw-hero-compact', 'pw-hero-fullbleed',
        'pw-journal-grid', 'pw-journal-featured', 'pw-media-library',
        'pw-the-wine-lineup', 'pw-the-wine-serve', 'pw-manifesto-band', 'pw-closing-cta',
      ];
      const PW_SELECTOR = [
        'section.pw-section', 'section.pw-hero', 'section.pw-bleed', 'section.pw-pullquote',
        ...PW_SECTION_NAMES.map(n => `section[class*="${n}--"]`),
      ].join(', ');

      const sectionConfigs = [
        // --- Dawn-native sections ---
        { selector: '.pw-hero-homepage',  setupFn: setup.hero,              threshold: 0.15 },
        { selector: '.multicolumn',       setupFn: setup.multicolumn,       threshold: 0.18 },
        { selector: '.collection',        setupFn: setup.featuredCollection, threshold: 0.15 },
        { selector: '.image-with-text',   setupFn: setup.imageWithText,     threshold: 0.2  },
        { selector: '.newsletter',        setupFn: setup.newsletter,        threshold: 0.2  },

        // --- Brand sections (paragraph-* family) ---
        { selector: '.pw-process',        setupFn: setup.process,        threshold: 0.18 },
        { selector: '.pw-pairings',       setupFn: setup.pairings,       threshold: 0.15 },
        { selector: '.paragraph-tasting',     setupFn: setup.tasting,        threshold: 0.18 },
        { selector: '.paragraph-winemaker',   setupFn: setup.winemaker,      threshold: 0.2  },
        { selector: '.pw-faq',            setupFn: setup.headerCascade,  threshold: 0.2  },
        { selector: '.pw-reviews',        setupFn: setup.headerCascade,  threshold: 0.2  },
        { selector: '.pw-closer',         setupFn: setup.headerCascade,  threshold: 0.18 },

        // --- Wireframe layer (pw-* family) — generic fade for editorial pages.
        { selector: PW_SELECTOR, setupFn: setup.pwGeneric, threshold: 0.2 },
      ];

      sectionConfigs.forEach(cfg => {
        // Use querySelectorAll so it works on pages with multiple instances
        const sections = document.querySelectorAll(cfg.selector);
        sections.forEach(section => {
          // Skip if already initialised (guard against double-init)
          if (section.hasAttribute('data-pm-init')) return;
          section.setAttribute('data-pm-init', '');

          const targets = cfg.setupFn(section);
          if (!targets || targets.length === 0) return;

          this._observe(targets, cfg.threshold);
        });
      });
    }

    /**
     * Create an IntersectionObserver for a set of targets.
     * @param {Array<{el: Element, type: string}>} targets
     * @param {number} threshold
     */
    _observe(targets, threshold) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;

          const el = entry.target;

          // For 'parent' type, the children animate via CSS parent selector
          el.classList.add('is-visible');
          clearWillChange(el);

          observer.unobserve(el);
        });
      }, {
        threshold,
        rootMargin: '0px 0px -40px 0px',
      });

      targets.forEach(t => observer.observe(t.el));
      this._observers.push(observer);
    }

    /** Instantly reveal everything (design mode / reduced motion) */
    _showEverything() {
      const all = document.querySelectorAll(
        '[data-pm], [data-pm-child], .pw-hero-homepage__content'
      );
      all.forEach(el => {
        el.classList.add('is-visible');
        el.classList.remove('pm-animating', 'pm-animating-clip');
        el.style.removeProperty('--pm-delay');
        el.style.removeProperty('--pm-dur');
      });
    }
  }

  // Register only once
  if (!customElements.get('paragraph-motion')) {
    customElements.define('paragraph-motion', ParagraphMotion);
  }
})();
